import React from 'react'
import { Box, Button, HStack, Text, VStack, type BoxProps } from '@chakra-ui/react'
import { MessageSquarePlus } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'

export type TimelineItem = {
  id: string
  sourceUpdateId?: number
  actor: string
  action: string
  timeLabel?: string
  type?: 'default' | 'done' | 'update'
  level?: number
}

type Props = {
  items: TimelineItem[]
  onAddComment?: () => void
  onReply?: (item: TimelineItem) => void
  onTagKb?: (item: TimelineItem) => void
  addCommentLabel?: string
} & BoxProps

function ActorBadge({
  actor,
  type,
  colors,
}: {
  actor: string
  type?: TimelineItem['type']
  colors: ReturnType<typeof useThemeColors>
}) {
  if (type === 'update') {
    const safe = (actor ?? '').trim()
    const initial = (safe[0] ?? '•').toUpperCase()
    return (
      <Box
        w="32px"
        h="32px"
        borderRadius="full"
        bg="blue.50"
        border="1px solid"
        borderColor="blue.200"
        display="grid"
        placeItems="center"
        flexShrink={0}
      >
        <Text fontWeight="800" color="blue.600" fontSize="sm" lineHeight="1">
          {initial}
        </Text>
      </Box>
    )
  }

  const safe = (actor ?? '').trim()
  const initial = (safe[0] ?? '•').toUpperCase()
  return (
    <Box
      w="32px"
      h="32px"
      borderRadius="full"
      bg={colors.inputBg ?? 'gray.50'}
      border="1px solid"
      borderColor={colors.borderLight}
      display="grid"
      placeItems="center"
      flexShrink={0}
    >
      <Text fontWeight="800" color={colors.textSecondary} fontSize="sm" lineHeight="1">
        {initial}
      </Text>
    </Box>
  )
}

export default function Timeline({ items, onAddComment, onReply, onTagKb, addCommentLabel = 'Add comment', ...boxProps }: Props) {
  const colors = useThemeColors()
  const count = items?.length ?? 0

  return (
    <Box
      bg={colors.cardBg}
      borderRadius="lg"
      border="1px solid"
      borderColor={colors.border}
      overflow="hidden"
      display="flex"
      flexDir="column"
      minH={0}
      {...boxProps}
    >
      <Box px={4} py={3} borderBottom="1px solid" borderColor={colors.borderLight}>
        <HStack justify="space-between" align="center">
          <Text fontWeight="700" color={colors.heading}>
            Ticket updates
          </Text>
          {onAddComment && (
            <Button
              size="xs"
              leftIcon={<MessageSquarePlus size={14} />}
              colorScheme="blue"
              variant="outline"
              onClick={onAddComment}
            >
              {addCommentLabel}
            </Button>
          )}
        </HStack>
      </Box>

      <Box px={4} py={4} flex="1" minH={0} overflowY="auto">
        {count ? (
          <VStack align="stretch" spacing={0}>
            {items.map((it, idx) => {
              const isLast = idx === count - 1
              return (
                <HStack key={it.id} align="stretch" spacing={3} pb={isLast ? 0 : 4}>
                  <Box w="32px" minW="32px" position="relative" ml={it.level ? `${it.level * 20}px` : 0}>
                    <ActorBadge actor={it.actor} type={it.type} colors={colors} />
                    {!isLast && (
                      <Box
                        position="absolute"
                        top="36px"
                        left="50%"
                        transform="translateX(-50%)"
                        w="2px"
                        bottom="0"
                        bg={colors.borderLight}
                        borderRadius="full"
                      />
                    )}
                  </Box>
                  <Box flex="1" minW={0} pt="1px">
                    <VStack align="start" spacing={1} minW={0}>
                      <Text fontSize="xs" color={colors.textMuted} fontWeight="600">
                        {it.timeLabel}
                      </Text>
                      <Text fontSize="sm" color={colors.text} whiteSpace="pre-wrap" fontFamily="mono">
                        {it.action}
                      </Text>
                      <HStack spacing={1}>
                        {onReply && (
                          <Button size="xs" variant="ghost" onClick={() => onReply(it)}>
                            Reply
                          </Button>
                        )}
                        {onTagKb && (
                          <Button size="xs" variant="ghost" onClick={() => onTagKb(it)}>
                            Tag as KB
                          </Button>
                        )}
                      </HStack>
                    </VStack>
                  </Box>
                </HStack>
              )
            })}
          </VStack>
        ) : (
          <Text fontSize="sm" color={colors.textSecondary}>
            No recent activity.
          </Text>
        )}
      </Box>
    </Box>
  )
}
